import type { ReactNode } from 'react';
import { ChevronDown, ChevronRight, Search, SlidersHorizontal } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Input } from '../ui/Input';
import { areaChipClasses, type AreaColorIndexMap } from '../../lib/areaColor';

/**
 * Always-visible 領域 (area) filter row. Sits above the collapsible
 * filter bar so the area chips are one click away even when the rest
 * of the filter panel is closed. The chip color follows the same
 * resolution as the WBS tree's area cell — explicit `colorIndex` from
 * the catalog when present, otherwise the deterministic name hash.
 */
export function WbsAreaFilter({
  areas,
  selected,
  onToggle,
  rightSlot,
  colorIndices,
  filterOpen,
  onToggleFilter,
  hasActiveFilter,
  hideDone,
  onToggleHideDone,
  delayed,
  onToggleDelayed,
  search,
  onSearchChange,
}: {
  areas: readonly string[];
  selected: readonly string[];
  onToggle: (area: string) => void;
  rightSlot?: ReactNode;
  /** `area-label -> stored colorIndex`. Optional — defaults to name hash. */
  colorIndices?: AreaColorIndexMap;
  /**
   * フィルタ toggle. When `onToggleFilter` is provided the button renders
   * as the leftmost control in this row (to the left of the 領域 chips)
   * and opens / closes the collapsible WbsFilterBar. `filterOpen` drives
   * the chevron + `aria-expanded`; `hasActiveFilter` drives the active
   * (dark) styling. The applied-count badge is intentionally omitted.
   */
  filterOpen?: boolean;
  onToggleFilter?: () => void;
  hasActiveFilter?: boolean;
  /** 完了以外 toggle state. When omitted the chip isn't rendered. */
  hideDone?: boolean;
  onToggleHideDone?: () => void;
  /** 遅延 toggle state. When omitted the chip isn't rendered. */
  delayed?: boolean;
  onToggleDelayed?: () => void;
  /**
   * Full-text search query. When `onSearchChange` is provided an
   * always-visible search box is rendered alongside the 領域 chips so
   * keyword filtering is reachable without opening the filter panel.
   */
  search?: string;
  onSearchChange?: (value: string) => void;
}) {
  if (
    areas.length === 0 &&
    !rightSlot &&
    !onToggleFilter &&
    !onToggleHideDone &&
    !onToggleDelayed &&
    !onSearchChange
  )
    return null;
  return (
    <div
      data-testid="wbs-area-filter"
      // Phase 1 responsive: keep the chips on a single row on narrow
      // viewports and let them scroll horizontally — wrapping made the
      // header bloom to 3+ rows on a phone, which crowded the tree out
      // of view.  `flex-nowrap` + `overflow-x-auto` is the same pattern
      // Asana / Linear use for filter chip rows on mobile.
      className="flex flex-nowrap items-center gap-1.5 overflow-x-auto border-b border-slate-200 bg-white px-4 py-2 text-xs sm:flex-wrap sm:overflow-visible"
    >
      {onToggleFilter ? (
        // Leftmost control: opens / closes the collapsible filter panel.
        // Replaces the old toolbar フィルタ button. No applied-count badge.
        <button
          type="button"
          data-testid="wbs-filter-toggle"
          onClick={onToggleFilter}
          className={cn(
            'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded border px-2 py-1 transition-colors',
            hasActiveFilter
              ? 'border-slate-900 bg-slate-900 text-white hover:bg-slate-800'
              : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-100',
          )}
          aria-expanded={!!filterOpen}
          aria-controls="wbs-filter-panel"
        >
          {filterOpen ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
          <SlidersHorizontal className="h-3.5 w-3.5" />
          <span>フィルタ</span>
        </button>
      ) : null}
      {onSearchChange ? (
        <div className="relative shrink-0">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="wbs-search-input"
            className="!h-7 !w-52 !py-0.5 pl-7 text-xs"
            placeholder="名前・内容で検索…"
            value={search ?? ''}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label="名前・内容で検索"
          />
        </div>
      ) : null}
      {areas.length > 0 ? (
        <span className="mr-1 shrink-0 text-slate-500">領域:</span>
      ) : null}
      {areas.map((area) => {
        const active = selected.includes(area);
        return (
          <button
            key={area}
            type="button"
            onClick={() => onToggle(area)}
            className={cn(
              'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-0.5 font-medium transition-colors',
              areaChipClasses(area, active, colorIndices),
            )}
            aria-pressed={active}
          >
            {area}
          </button>
        );
      })}
      {(onToggleHideDone || onToggleDelayed) ? (
        <span className="ml-1 mr-1 shrink-0 text-slate-500">ステータス:</span>
      ) : null}
      {onToggleHideDone ? (
        // 完了以外 toggle — sits to the right of the 領域 chips as its
        // own independent pill. Uses the indigo accent (not the area
        // palette) so it reads as a distinct "hide done" control rather
        // than another 領域 option.
        <button
          type="button"
          data-testid="wbs-hide-done-toggle"
          onClick={onToggleHideDone}
          className={cn(
            'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-0.5 font-medium transition-colors',
            hideDone
              ? 'border-indigo-300 bg-indigo-100 text-indigo-700'
              : 'border-slate-200 bg-white text-slate-500 hover:border-slate-400',
          )}
          aria-pressed={!!hideDone}
        >
          完了以外
        </button>
      ) : null}
      {onToggleDelayed ? (
        // 遅延 toggle — sits immediately right of 完了以外. Uses the
        // same red palette as the overdue Gantt bar so the affordance
        // matches what users already learnt visually.
        <button
          type="button"
          data-testid="wbs-delayed-toggle"
          onClick={onToggleDelayed}
          className={cn(
            'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-0.5 font-medium transition-colors',
            delayed
              ? 'border-red-300 bg-red-100 text-red-700'
              : 'border-slate-200 bg-white text-slate-500 hover:border-slate-400',
          )}
          aria-pressed={!!delayed}
        >
          遅延
        </button>
      ) : null}
      {rightSlot ? (
        <div className="ml-auto shrink-0">{rightSlot}</div>
      ) : null}
    </div>
  );
}
